import { trackSceneAction } from 'analytics';
import forge from 'apis/forge';
import ShareSceneModal from 'components/Modal/Share/ShareSceneModal';
import { Scene } from 'dragonmouse-shared/interfaces';
import * as api from 'dragonmouse-shared/api/forge';
import { MenuItem, MenuItemAction, MenuItemDivider } from 'menu/MenuItem';
import { useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { showContextMenu } from 'redux/mode/actions';
import { displayConfirmModal } from 'redux/mode/util';
import { RootReducer } from 'redux/reducers';
import { selectIsMobile } from 'redux/mode/reducers';
import { useMobileTapBehavior } from 'hooks/useMobileTapBehavior';
import * as routes from 'routes';
import * as S from './styles';
import { useNavigate } from 'react-router';

interface Props {
	scene: Scene;
	isActive?: boolean;
	onClick?: () => void;
	onCommandClick?: () => void;
	onDuplicateProject?: (sceneId: string) => void;
}

const ProjectListItem = ({
	scene,
	isActive,
	onClick,
	onCommandClick,
	onDuplicateProject,
}: Props) => {
	const dispatch = useDispatch();
	const [showShareModal, setShowShareModal] = useState(false);
	const navigate = useNavigate();
	const { userEmail, isMobile, activeSceneId } = useSelector(
		(r: RootReducer) => ({
			userEmail: r.mode.authUser?.email,
			isMobile: selectIsMobile(r.mode),
			activeSceneId: r.mode.activeSceneId,
		}),
		shallowEqual
	);

	const { isPressed, touchHandlers } = useMobileTapBehavior(isMobile);

	const menuItems: MenuItem[] = [
		new MenuItemAction('Open project', '', () => {
			trackSceneAction('open');
			onClick && onClick();
		}),
		new MenuItemAction('Open project in new tab', '', () => {
			trackSceneAction('open_in_new_tab');
			window.open(routes.scene(scene.id, scene.title), '_blank', 'noopener,noreferrer');
		}),
	];

	if (scene && !scene.viewOnly) {
		menuItems.push(
			new MenuItemDivider(),
			MenuItemAction.createRenameScene(scene),
			new MenuItemAction('Delete project...', '', async () => {
				const users = await forge.getSceneUsers({ sceneId: scene.id });

				if (users.emails.length === 1) {
					displayConfirmModal({
						title: 'Delete project',
						description: 'Are you sure you want to delete this project?',
						submitLabel: 'Delete',
						onCancel: () => {},
						onSubmit: async () => {
							await forge.deleteScene(
								{
									sceneId: scene.id,
								},
								true
							);
							// scene is gone! take user to the "new projects" page
							if (scene.id === activeSceneId) navigate(routes.home(), { replace: true });
						},
					});
				} else {
					const baselineMessage = `${users.emails.length - 1} other user${
						users.emails.length - 1 === 1 ? ' has' : 's have'
					} access to this project. If you delete it, they will no longer have access. Are you sure you want to delete this project?`;
					displayConfirmModal({
						title: 'Delete project',
						description: userEmail
							? `${baselineMessage} Or would you rather remove yourself from the project?`
							: baselineMessage,
						submitLabel: 'Delete',
						altActionLabel: 'Remove myself',
						onCancel: () => {},
						onSubmit: async () => {
							await forge.deleteScene(
								{
									sceneId: scene.id,
								},
								true
							);
							// scene is gone! take user to the "new projects" page
							if (scene.id === activeSceneId) navigate(routes.home(), { replace: true });
						},
						onAltAction: userEmail
							? async () => {
									const req: api.RevokeSceneAccessRequest = {
										sceneId: scene.id,
										email: userEmail,
									};
									await forge.revokeSceneAccess.withToast()(req);
							  }
							: undefined,
					});
				}
			}),
			new MenuItemDivider(),
			new MenuItemAction('Duplicate project', '', () => {
				trackSceneAction('duplicate');
				onDuplicateProject && onDuplicateProject(scene.id);
			})
		);
	}

	menuItems.push(
		new MenuItemDivider(),
		new MenuItemAction('Share...', '', () => {
			trackSceneAction('open_share_modal');
			setShowShareModal(true);
		})
	);

	const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		if (e.metaKey || e.ctrlKey) {
			trackSceneAction('open_in_new_tab');
			onCommandClick && onCommandClick();
		} else if (onClick) {
			trackSceneAction('open');
			onClick();
		}
	};

	const onContextMenu = (e: React.MouseEvent) => {
		if (isMobile) return;
		dispatch(
			showContextMenu({
				point: { x: e.clientX + 5, y: e.clientY },
				menuItems: menuItems,
			})
		);
	};

	const handleContainerTap = () => {
		if (onClick) {
			trackSceneAction('open');
			onClick();
		}
	};

	return (
		<S.Base
			isActive={isActive}
			onClick={isMobile ? handleContainerTap : handleClick}
			onContextMenu={isMobile ? undefined : onContextMenu}
			title={scene.title}
			data-pressed={isPressed}
			{...touchHandlers}
		>
			<S.Title isActive={isActive}>{scene.title}</S.Title>
			{showShareModal && <ShareSceneModal scene={scene} onClose={() => setShowShareModal(false)} />}
		</S.Base>
	);
};

export default ProjectListItem;
